import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../utils/api';
import { shouldSuppressErrorForAuth } from '../utils/authErrors';
import { useToast } from '../components/Toast';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { ConfirmModal } from '../components/ConfirmModal';
import { ClientArrivalToolbar } from '../components/reception/ClientArrivalToolbar';
import { AppointmentQuickFind } from '../components/reception/AppointmentQuickFind';
import { AppointmentStatusBadge } from '../components/appointment/AppointmentStatusBadge';
import { RefreshWidget } from '../components/RefreshWidget';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useRuntimeSettings } from '../hooks/useRuntimeSettings';
import { isMinor as isMinorFromBirthDate } from '@digitaltattoo/shared-utils';

// ── Types ─────────────────────────────────────────────────────────────────────
interface ActSummary {
  id: string;
  sequence: number;
  status: string;
  bodyZoneName?: string | null;
  piercingTypeName?: string | null;
  demandDetails?: {
    isMinor?: boolean;
    guardianFirstName?: string;
    guardianLastName?: string;
    bodyZoneId?: string;
    piercingTypeModelId?: string;
    productType?: string;
    reference?: string;
    salesReference?: string;
    productCode?: string;
    sku?: string;
    jewelry?: {
      material?: string;
      option?: string;
      price?: number;
      type?: string;
      productType?: string;
      reference?: string;
      productCode?: string;
      sku?: string;
    };
    notes?: string;
  } | null;
}

interface AppointmentItem {
  id: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientBirthDate?: string | null;
  isMinor?: boolean | null;
  serviceName: string;
  serviceCategory: string;
  status: string;
  scheduledAt: string;
  bookingSource?: string;
  shopName: string;
  practitionerId?: string | null;
  assignedPractitionerFirstName?: string | null;
  assignedPractitionerLastName?: string | null;
  notes?: string | null;
  acts: ActSummary[];
  pricing?: Record<string, unknown>;
}

interface DashboardStats { toReceive: number; inShop: number; awaitingCheckout: number; totalToday: number; }

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(first: string, last: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}
function formatTime(iso: string) {
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
}

// ── Sub-components ────────────────────────────────────────────────────────────
function SourceBadge({ source }: { source?: string }) {
  const s = (source ?? '').toUpperCase();
  const map: Record<string, { label: string; cls: string }> = {
    ONLINE:     { label: 'En ligne',    cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    WALK_IN:    { label: 'Walk-in',     cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    BACKOFFICE: { label: 'Tél / Admin', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  };
  const cfg = map[s] ?? { label: s || '—', cls: 'bg-gray-100 text-gray-500 border-gray-200' };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function AgeBadge({
  birthDate,
  isMinor,
}: {
  birthDate?: string | null;
  isMinor?: boolean | null;
}) {
  const minor = birthDate ? isMinorFromBirthDate(birthDate) : isMinor;
  if (minor === null || minor === undefined) return null;
  return minor
    ? <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-200">Mineur</span>
    : <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border bg-green-100 text-green-700 border-green-200">Adulte</span>;
}

function StatusBadge({ status }: { status: string }) {
  return <AppointmentStatusBadge status={status} />;
}

function AppointmentCard({
  appointment,
  onRequestCheckIn,
  onRequestUndoCheckIn,
  onViewAppointment,
  loading,
}: {
  appointment: AppointmentItem;
  onRequestCheckIn: (a: AppointmentItem) => void;
  onRequestUndoCheckIn?: (a: AppointmentItem) => void;
  onViewAppointment?: (a: AppointmentItem) => void;
  loading: boolean;
}) {
  const canCheckIn = ['SCHEDULED', 'CONFIRMED'].includes(appointment.status);
  const canUndoCheckIn = appointment.status === 'CHECKED_IN';
  const isInShop   = ['CHECKED_IN', 'IN_PROGRESS'].includes(appointment.status);

  const borderCls = isInShop ? 'border-l-4 border-amber-400' : 'border-l-4 border-primary-300';
  const avatarCls = isInShop ? 'bg-amber-100 text-amber-700' : 'bg-primary-100 text-primary-600';

  return (
    <div className={`card flex flex-col gap-4 ${borderCls}`}>
      {/* Header: avatar + name + email */}
      <div className="flex gap-3 items-start">
        <div className={`w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-bold ${avatarCls}`}>
          {initials(appointment.clientFirstName, appointment.clientLastName)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm text-textPrimary truncate">
            {appointment.clientFirstName} {appointment.clientLastName}
          </p>
          <p className="text-xs text-textSecondary truncate">{appointment.clientEmail}</p>
        </div>
      </div>

      {/* Meta pills */}
      <div className="flex flex-wrap gap-1.5">
        <StatusBadge status={appointment.status} />
        <SourceBadge source={appointment.bookingSource} />
        <AgeBadge birthDate={appointment.clientBirthDate} isMinor={appointment.isMinor} />
      </div>

      {/* Service + acts summary */}
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
          {appointment.serviceName}
        </p>
        {appointment.acts.length > 0 && (
          <div className="space-y-1">
            {appointment.acts.map((act) => (
              <div key={act.id} className="flex items-center gap-2 text-sm">
                <span className="w-5 h-5 rounded-full bg-white border border-gray-200 text-gray-500 text-xs flex items-center justify-center font-bold flex-shrink-0">
                  {act.sequence ?? '?'}
                </span>
                <span className="text-gray-700">
                  {act.piercingTypeName ?? '—'}
                  {act.bodyZoneName && <span className="text-gray-400"> · {act.bodyZoneName}</span>}
                </span>
                {act.demandDetails?.isMinor && (
                  <span className="text-xs text-red-600 font-medium">(mineur)</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shop + time + CTA */}
      <div className="flex items-end justify-between gap-2 pt-1 border-t border-gray-100">
        <div className="space-y-0.5 text-xs">
          <p className="text-gray-400 uppercase tracking-wider font-semibold">
            {isInShop ? 'Arrivée enregistrée' : 'Prévu à'}
          </p>
          <p className="font-bold text-sm text-gray-800">{formatTime(appointment.scheduledAt)}</p>
          {appointment.shopName && (
            <p className="text-gray-500">{appointment.shopName}</p>
          )}
          {appointment.assignedPractitionerFirstName && (
            <p className="text-gray-400">
              Praticien : {appointment.assignedPractitionerFirstName} {appointment.assignedPractitionerLastName}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {canCheckIn && (
            <button
              type="button"
              onClick={() => onRequestCheckIn(appointment)}
              disabled={loading}
              className="btn-primary-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center gap-1.5">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                  <span>Traitement…</span>
                </div>
              ) : (
                'Enregistrer l\'arrivée'
              )}
            </button>
          )}
          {canUndoCheckIn && onRequestUndoCheckIn && (
            <button
              type="button"
              onClick={() => onRequestUndoCheckIn(appointment)}
              disabled={loading}
              className="btn-secondary-sm text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler l&apos;enregistrement
            </button>
          )}
          {onViewAppointment && (
            <button
              type="button"
              onClick={() => onViewAppointment(appointment)}
              disabled={loading}
              className="btn-secondary-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Voir rendez-vous
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
function pricingIndicativeEuros(pricing?: Record<string, unknown>): string {
  if (!pricing) return '';
  const base = Number(pricing.basePrice);
  if (!Number.isNaN(base) && base >= 0) return base.toFixed(0);
  return '';
}

function formatCheckoutEuros(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const r = Math.round(n * 100) / 100;
  return Number.isInteger(r) ? String(r) : r.toFixed(2);
}

type CheckoutPromotionType = 'PERCENT' | 'AMOUNT';

function getCheckoutBaseAmount(appointment: AppointmentItem): number {
  const totalDue = buildEmployeeCheckoutPricing(appointment).totalDue;
  if (totalDue > 0) return totalDue;
  const indicative = Number(pricingIndicativeEuros(appointment.pricing));
  return Number.isFinite(indicative) && indicative > 0 ? indicative : 0;
}

function getAmountAfterPromotion(
  baseAmount: number,
  type: CheckoutPromotionType,
  rawValue: string,
): string {
  const value = Number(rawValue.trim().replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) return formatCheckoutEuros(baseAmount);
  const promotionAmount = type === 'PERCENT' ? baseAmount * Math.min(value, 100) / 100 : value;
  return formatCheckoutEuros(Math.max(0, baseAmount - promotionAmount));
}

type PiercingTypeRow = {
  id?: string;
  name?: string;
  price?: number;
  type?: string;
  productType?: string;
  reference?: string;
  salesReference?: string;
  productCode?: string;
  sku?: string;
};

type CheckoutProductLine = {
  label: string;
  amount: number;
  productType: string;
  reference: string;
};

function firstCheckoutText(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function checkoutMoney(value: unknown): number {
  if (value == null) return 0;
  const num = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(num) ? num : 0;
}

function getPiercingRowAmount(row: PiercingTypeRow): number {
  const record = row as Record<string, unknown>;
  const keys = ['price', 'basePrice', 'amount', 'unitPrice', 'piercingPrice', 'customPrice'];
  for (const key of keys) {
    const amount = Math.max(0, checkoutMoney(record[key]));
    if (amount > 0) return amount;
  }
  return 0;
}

/** Même logique que la finalisation praticien : actes + prix résa, bijoux, suppléments, acompte, reste à payer. */
function buildEmployeeCheckoutPricing(appointment: AppointmentItem): {
  piercingLines: CheckoutProductLine[];
  piercingSubtotal: number;
  jewelryLines: CheckoutProductLine[];
  jewelryTotal: number;
  supplementLines: Array<CheckoutProductLine & { unitPrice: number; qty: number }>;
  supplementsTotal: number;
  legacySupplementLines: Array<CheckoutProductLine & { unitPrice: number; qty: number }>;
  legacySupplementsTotal: number;
  deposit: number;
  totalDue: number;
} {
  const pricing = (appointment.pricing ?? {}) as Record<string, unknown>;
  const acts = appointment.acts;
  const pts = (Array.isArray(pricing.piercingTypes) ? pricing.piercingTypes : []) as PiercingTypeRow[];
  const nBooked = pts.length;
  const sumBooked = pts.reduce((s, x) => s + getPiercingRowAmount(x), 0);
  const baseFallback =
    Number(pricing.basePrice) || Number(pricing.total) || Number(pricing.subtotal) || 0;
  const avgLine =
    nBooked > 0 ? sumBooked / nBooked : acts.length > 0 ? baseFallback / acts.length : 0;

  const piercingLines: CheckoutProductLine[] = [];
  for (let i = 0; i < acts.length; i++) {
    const act = acts[i];
    const tid = act.demandDetails?.piercingTypeModelId;
    const ptByIndex = i < nBooked ? pts[i] : undefined;
    const ptByType = tid ? pts.find((x) => x.id === tid) : undefined;
    let amount = 0;
    let nameBit = act.piercingTypeName ?? 'Piercing';
    if (ptByType != null && getPiercingRowAmount(ptByType) > 0) {
      amount = getPiercingRowAmount(ptByType);
      nameBit = ptByType.name || nameBit;
    } else if (ptByIndex != null && getPiercingRowAmount(ptByIndex) > 0) {
      amount = getPiercingRowAmount(ptByIndex);
      nameBit = ptByIndex.name || nameBit;
    } else {
      amount = avgLine;
      nameBit = i >= nBooked ? 'Piercing (acte ajouté)' : nameBit;
    }
    piercingLines.push({
      label: `Acte ${act.sequence} — ${nameBit}`,
      amount: Math.max(0, amount),
      productType: firstCheckoutText(
        ptByType?.productType,
        ptByType?.type,
        ptByIndex?.productType,
        ptByIndex?.type,
        act.demandDetails?.productType,
      ) ?? 'Prestation',
      reference: firstCheckoutText(
        ptByType?.productCode,
        ptByType?.reference,
        ptByType?.salesReference,
        ptByType?.sku,
        ptByIndex?.productCode,
        ptByIndex?.reference,
        ptByIndex?.salesReference,
        ptByIndex?.sku,
        act.demandDetails?.productCode,
        act.demandDetails?.reference,
        act.demandDetails?.salesReference,
        act.demandDetails?.sku,
        act.piercingTypeName,
        nameBit,
      ) ?? 'Non renseignée',
    });
  }
  if (acts.length === 0 && pts.length > 0) {
    pts.forEach((pt, idx) => {
      piercingLines.push({
        label: pt.name ?? `Prestation ${idx + 1}`,
        amount: Math.max(0, getPiercingRowAmount(pt)),
        productType: firstCheckoutText(pt.productType, pt.type) ?? 'Prestation',
        reference: firstCheckoutText(
          pt.productCode,
          pt.reference,
          pt.salesReference,
          pt.sku,
          pt.name,
        ) ?? 'Non renseignée',
      });
    });
  }
  const storedReference =
    checkoutMoney(pricing.subtotal) > 0
      ? checkoutMoney(pricing.subtotal)
      : checkoutMoney(pricing.total) > 0
        ? checkoutMoney(pricing.total)
        : checkoutMoney(pricing.basePrice);

  if (piercingLines.length === 0 && storedReference > 0) {
    piercingLines.push({
      label: 'Montant réservation',
      amount: storedReference,
      productType: 'Prestation',
      reference: 'Non renseignée',
    });
  }

  let piercingSubtotal = piercingLines.reduce((s, l) => s + l.amount, 0);
  if (piercingLines.length > 0 && storedReference > piercingSubtotal + 0.02) {
    const missing = storedReference - piercingSubtotal;
    const zeroIdx = piercingLines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => line.amount < 0.005)
      .map(({ index }) => index);
    const targetIdx = zeroIdx.length > 0 ? zeroIdx : piercingLines.map((_, index) => index);
    const addEach = missing / targetIdx.length;
    targetIdx.forEach((index) => {
      const line = piercingLines[index];
      piercingLines[index] = { ...line, amount: Math.max(0, line.amount + addEach) };
    });
    piercingSubtotal = piercingLines.reduce((s, l) => s + l.amount, 0);
  }

  const jewelryLines: CheckoutProductLine[] = [];
  for (const act of acts) {
    const j = act.demandDetails?.jewelry;
    if (!j) continue;
    const price = Math.max(0, Number(j.price) || 0);
    const name = j.material ? `Bijou — ${j.material}` : `Bijou (acte ${act.sequence})`;
    jewelryLines.push({
      label: name,
      amount: price,
      productType: firstCheckoutText(j.productType, j.type) ?? 'Bijou',
      reference: firstCheckoutText(j.productCode, j.reference, j.sku) ?? 'Non renseignée',
    });
  }
  const jewelryTotal = jewelryLines.reduce((s, l) => s + l.amount, 0);

  const rawSups = Array.isArray(pricing.workflowSupplements)
    ? (pricing.workflowSupplements as unknown[])
    : [];
  const supplementLines: Array<CheckoutProductLine & { unitPrice: number; qty: number }> = [];
  for (const x of rawSups as Array<Record<string, unknown>>) {
    if (!x) continue;
    const name = firstCheckoutText(x.name, x.label, x.productCode, x.reference) ?? '';
    const qty = Math.max(1, Math.floor(Number(x.qty) || 1));
    const hasPrice = x.price != null && x.price !== '';
    const amountValue = Math.max(0, Number(x.amount) || 0);
    const unitPrice = Math.max(0, Number(hasPrice ? x.price : amountValue) || 0);
    const lineAmount = hasPrice ? unitPrice * qty : amountValue;
    if (!name && lineAmount <= 0) continue;
    supplementLines.push({
      label: name || 'Produit additionnel',
      amount: lineAmount,
      unitPrice,
      qty,
      productType: firstCheckoutText(x.productType, x.type, x.category) ?? 'Produit additionnel',
      reference: firstCheckoutText(
        x.productCode,
        x.reference,
        x.salesReference,
        x.sku,
      ) ?? 'Non renseignée',
    });
  }
  const supplementsTotal = supplementLines.reduce((s, l) => s + l.amount, 0);

  const rawLegacySups = Array.isArray(pricing.supplements)
    ? (pricing.supplements as unknown[])
    : [];
  const legacySupplementLines: Array<CheckoutProductLine & { unitPrice: number; qty: number }> = [];
  for (const x of rawLegacySups as Array<Record<string, unknown>>) {
    if (!x) continue;
    const name = firstCheckoutText(x.name, x.label, x.productCode, x.reference) ?? '';
    const qty = Math.max(1, Math.floor(Number(x.qty) || 1));
    const hasPrice = x.price != null && x.price !== '';
    const amountValue = Math.max(0, Number(x.amount) || 0);
    const unitPrice = Math.max(0, Number(hasPrice ? x.price : amountValue) || 0);
    const lineAmount = hasPrice ? unitPrice * qty : amountValue;
    if (!name && lineAmount <= 0) continue;
    legacySupplementLines.push({
      label: name || 'Supplément',
      amount: lineAmount,
      unitPrice,
      qty,
      productType: firstCheckoutText(x.productType, x.type, x.category) ?? 'Supplément réservation',
      reference: firstCheckoutText(
        x.productCode,
        x.reference,
        x.salesReference,
        x.sku,
        x.id,
      ) ?? 'Non renseignée',
    });
  }
  const legacySupplementsTotal = legacySupplementLines.reduce((s, l) => s + l.amount, 0);

  const deposit = Number(pricing.depositAmount ?? pricing.deposit ?? 0) || 0;
  const totalDue = Math.max(
    0,
    piercingSubtotal + jewelryTotal + supplementsTotal + legacySupplementsTotal - deposit,
  );

  return {
    piercingLines,
    piercingSubtotal,
    jewelryLines,
    jewelryTotal,
    supplementLines,
    supplementsTotal,
    legacySupplementLines,
    legacySupplementsTotal,
    deposit,
    totalDue,
  };
}

function CheckoutProductLineDetails({
  line,
  label,
}: {
  line: CheckoutProductLine;
  label: React.ReactNode;
}) {
  return (
    <>
      <div className="min-w-0">
        <span className="block truncate">{label}</span>
        <span className="block text-[11px] leading-4 text-gray-500">
          Type de produit : <span className="font-medium text-gray-700">{line.productType}</span> <br />
          Référence : <span className="font-medium text-gray-700">{line.reference}</span>
        </span>
      </div>
      <span className="font-medium tabular-nums shrink-0 text-gray-900">
        {formatCheckoutEuros(line.amount)} €
      </span>
    </>
  );
}

function CheckoutPricingBreakdown({ appointment }: { appointment: AppointmentItem }) {
  const s = buildEmployeeCheckoutPricing(appointment);
  const hasDetail =
    s.piercingLines.length > 0 ||
    s.jewelryLines.length > 0 ||
    s.supplementLines.length > 0 ||
    s.legacySupplementLines.length > 0 ||
    s.deposit > 0;

  if (!hasDetail) {
    const hint = pricingIndicativeEuros(appointment.pricing);
    if (hint === '') return null;
    return (
      <p className="text-xs text-violet-700 font-medium mt-1">
        Indicatif réservation : {hint} €
      </p>
    );
  }

  return (
    <div className="mt-1 space-y-2 text-xs">
      {s.piercingLines.length > 0 && (
        <ul className="space-y-1">
          {s.piercingLines.map((l, i) => (
            <li key={`p-${i}`} className="flex items-start justify-between gap-2 text-gray-800">
              <CheckoutProductLineDetails line={l} label={l.label} />
            </li>
          ))}
        </ul>
      )}
      {s.jewelryLines.length > 0 && (
        <ul className="space-y-1">
          {s.jewelryLines.map((l, i) => (
            <li key={`j-${i}`} className="flex items-start justify-between gap-2 text-gray-700">
              <CheckoutProductLineDetails line={l} label={l.label} />
            </li>
          ))}
        </ul>
      )}
      {s.supplementLines.length > 0 && (
        <ul className="space-y-1">
          {s.supplementLines.map((l, i) => (
            <li key={`s-${i}`} className="flex items-start justify-between gap-2 text-gray-700">
              <CheckoutProductLineDetails
                line={l}
                label={`${l.label}${l.qty > 1 ? ` × ${l.qty}` : ''}`}
              />
            </li>
          ))}
        </ul>
      )}
      {s.legacySupplementLines.length > 0 && (
        <ul className="space-y-1">
          {s.legacySupplementLines.map((l, i) => (
            <li key={`ls-${i}`} className="flex items-start justify-between gap-2 text-gray-700">
              <CheckoutProductLineDetails
                line={l}
                label={`${l.label}${l.qty > 1 ? ` × ${l.qty}` : ''}`}
              />
            </li>
          ))}
        </ul>
      )}
      {s.deposit > 0 && (
        <div className="flex justify-between gap-2 text-emerald-800 font-medium">
          <span>Acompte / prépaiement</span>
          <span className="tabular-nums">− {formatCheckoutEuros(s.deposit)} €</span>
        </div>
      )}
      <div className="flex justify-between gap-2 pt-1 border-t border-violet-100 text-sm font-bold text-violet-900">
        <span>Reste à encaisser</span>
        <span className="tabular-nums">{formatCheckoutEuros(s.totalDue)} €</span>
      </div>
    </div>
  );
}

export type ReceptionDashboardMode = 'employee' | 'admin';

export interface DashboardEmployeePageProps {
  mode?: ReceptionDashboardMode;
  /** When true, hides the page title block (used inside accueil client). */
  embedded?: boolean;
  title?: string;
  /** Increment to trigger a dashboard reload from parent. */
  refreshToken?: number;
  /** Filter admin reception dashboard to a single shop. */
  shopId?: string | null;
}

function getReceptionApi(mode: ReceptionDashboardMode) {
  if (mode === 'admin') {
    return {
      dashboard: '/api/admin/reception/dashboard',
      checkIn: (id: string) => `/api/admin/reception/appointments/${id}/check-in`,
      undoCheckIn: (id: string) => `/api/admin/reception/appointments/${id}/undo-check-in`,
      completeCheckout: (id: string) => `/api/admin/reception/appointments/${id}/complete-checkout`,
    };
  }
  return {
    dashboard: '/api/employee/dashboard',
    checkIn: (id: string) => `/api/employee/appointments/${id}/check-in`,
    undoCheckIn: (id: string) => `/api/employee/appointments/${id}/undo-check-in`,
    completeCheckout: (id: string) => `/api/employee/appointments/${id}/complete-checkout`,
  };
}

export function DashboardEmployeePage({
  mode = 'employee',
  embedded = false,
  title,
  refreshToken = 0,
  shopId = null,
}: DashboardEmployeePageProps = {}) {
  const { showToast, ToastComponent } = useToast();
  const { currentShopId: contextShopId, currentShop, loading: shopLoading } = useCurrentShop();
  const { autoRefreshIntervalSeconds } = useRuntimeSettings();
  const resolvePath =
    mode === 'admin' ? '/api/admin/reception/resolve-qr' : '/api/employee/resolve-qr';
  const effectiveShopId = mode === 'admin' ? shopId : contextShopId;
  const [toReceive, setToReceive]         = useState<AppointmentItem[]>([]);
  const [inShop, setInShop]               = useState<AppointmentItem[]>([]);
  const [awaitingCheckout, setAwaitingCheckout] = useState<AppointmentItem[]>([]);
  const [stats, setStats]                 = useState<DashboardStats>({ toReceive: 0, inShop: 0, awaitingCheckout: 0, totalToday: 0 });
  const [loadingDash, setLoadingDash] = useState(true);
  const [dashError, setDashError]     = useState<string | null>(null);
  const [arrivalError, setArrivalError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [checkInConfirm, setCheckInConfirm] = useState<AppointmentItem | null>(null);
  const [undoCheckInConfirm, setUndoCheckInConfirm] = useState<AppointmentItem | null>(null);
  const [checkoutTarget, setCheckoutTarget] = useState<AppointmentItem | null>(null);
  const [checkoutPromotionType, setCheckoutPromotionType] = useState<CheckoutPromotionType>('PERCENT');
  const [checkoutPromotionValue, setCheckoutPromotionValue] = useState('');
  const [checkoutAmount, setCheckoutAmount] = useState('');
  const [checkoutMethod, setCheckoutMethod] = useState<'CASH' | 'CARD'>('CASH');
  const [checkoutNotes, setCheckoutNotes]   = useState('');
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);

  const api = getReceptionApi(mode);
  const pageTitle = title ?? (mode === 'admin' ? 'File d\'attente — Accueil' : 'Tableau de bord réception');

  const loadDashboard = useCallback(async (showInitialLoader = false) => {
    try {
      if (showInitialLoader) {
        setLoadingDash(true);
      }
      setDashError(null);
      const res  = await apiClient(api.dashboard);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Erreur API');
      const d = data.data;
      setToReceive(d.toReceive ?? []);
      setInShop(d.inShop ?? []);
      setAwaitingCheckout(d.awaitingCheckout ?? []);
      setStats(
        d.stats ?? { toReceive: 0, inShop: 0, awaitingCheckout: 0, totalToday: 0 },
      );
    } catch (err) {
      console.error('Employee dashboard error:', err);
      if (shouldSuppressErrorForAuth(err)) return;
      setDashError('Impossible de charger le tableau de bord.');
    } finally {
      if (showInitialLoader) {
        setLoadingDash(false);
      }
    }
  }, [api.dashboard, mode, effectiveShopId]);

  // When embedded, the parent page owns the visible auto-refresh indicator and
  // drives reloads via `refreshToken` — avoid a second, invisible background timer here.
  const { refreshNow, refreshing, lastRefreshAt } = useAutoRefresh({
    intervalSeconds: autoRefreshIntervalSeconds,
    enabled: !embedded && !shopLoading && !!effectiveShopId,
    onRefresh: () => loadDashboard(false),
  });

  useEffect(() => {
    if (shopLoading) return;
    if (!effectiveShopId) return;
    void loadDashboard(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDashboard, mode, effectiveShopId, shopLoading]);

  // External refresh requests (e.g. a manual button in a parent page) reload
  // in the background — no full-page spinner, so open modals stay open.
  const isFirstRefreshToken = React.useRef(true);
  useEffect(() => {
    if (isFirstRefreshToken.current) {
      isFirstRefreshToken.current = false;
      return;
    }
    if (shopLoading || !effectiveShopId) return;
    void loadDashboard(false);
  }, [refreshToken, loadDashboard, effectiveShopId, shopLoading]);

  const handleCheckIn = async (appointmentId: string) => {
    try {
      setActionLoading(appointmentId);
      const res    = await apiClient(api.checkIn(appointmentId), { method: 'POST', body: '{}' });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      showToast('Arrivée enregistrée avec succès', 'success');
      setCheckInConfirm(null);
      await loadDashboard(false);
    } catch (err: any) {
      console.error('Check-in error:', err);
      if (!shouldSuppressErrorForAuth(err)) {
        showToast(err?.message || 'Erreur lors de l\'enregistrement', 'error', 6000);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const openCheckoutModal = (a: AppointmentItem) => {
    setCheckoutPromotionType('PERCENT');
    setCheckoutPromotionValue('');
    setCheckoutAmount(formatCheckoutEuros(getCheckoutBaseAmount(a)));
    setCheckoutMethod('CASH');
    setCheckoutNotes('');
    setCheckoutTarget(a);
  };

  const handleCompleteCheckout = async () => {
    if (!checkoutTarget) return;
    const promotionRaw = checkoutPromotionValue.trim().replace(',', '.');
    const promotionValue = promotionRaw === '' ? 0 : Number(promotionRaw);
    const baseAmount = getCheckoutBaseAmount(checkoutTarget);
    if (promotionRaw !== '' && (!Number.isFinite(promotionValue) || promotionValue < 0)) {
      showToast('Promotion invalide', 'error', 4000);
      return;
    }
    if (checkoutPromotionType === 'PERCENT' && promotionValue > 100) {
      showToast('Le pourcentage de promotion ne peut pas dépasser 100 %', 'error', 4000);
      return;
    }
    if (checkoutPromotionType === 'AMOUNT' && promotionValue > baseAmount) {
      showToast('La promotion ne peut pas dépasser le montant à encaisser', 'error', 4000);
      return;
    }
    const raw = checkoutAmount.trim().replace(',', '.');
    const amount = raw === '' ? 0 : Number(raw);
    if (raw !== '' && !Number.isFinite(amount)) {
      showToast('Montant invalide', 'error', 4000);
      return;
    }
    if (amount < 0) {
      showToast('Le montant ne peut pas être négatif', 'error', 4000);
      return;
    }
    try {
      setCheckoutSubmitting(true);
      const res = await apiClient(api.completeCheckout(checkoutTarget.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          method: checkoutMethod,
          ...(promotionValue > 0
            ? {
                promotion: {
                  type: checkoutPromotionType,
                  value: promotionValue,
                  appliedAmount: checkoutPromotionType === 'PERCENT'
                    ? Math.round((baseAmount * promotionValue / 100) * 100) / 100
                    : promotionValue,
                },
              }
            : {}),
          ...(checkoutNotes.trim() ? { notes: checkoutNotes.trim() } : {}),
        }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      showToast('Paiement enregistré — rendez-vous clôturé', 'success');
      setCheckoutTarget(null);
      await loadDashboard(false);
    } catch (err: any) {
      console.error('Checkout error:', err);
      if (!shouldSuppressErrorForAuth(err)) {
        showToast(err?.message || 'Erreur lors de l’encaissement', 'error', 6000);
      }
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  const handleUndoCheckIn = async (appointmentId: string) => {
    try {
      setActionLoading(appointmentId);
      const res    = await apiClient(api.undoCheckIn(appointmentId), { method: 'POST', body: '{}' });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      showToast('Enregistrement d\'arrivée annulé', 'success');
      setUndoCheckInConfirm(null);
      await loadDashboard(false);
    } catch (err: any) {
      console.error('Undo check-in error:', err);
      if (!shouldSuppressErrorForAuth(err)) {
        showToast(err?.message || 'Erreur lors de l\'annulation', 'error', 6000);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewAppointment = (appointmentId: string) => {
    window.open(`/appointments/${appointmentId}`, '_blank', 'noopener,noreferrer');
  };

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  if (shopLoading || loadingDash) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!effectiveShopId) {
    return (
      <div className="card text-center py-10 text-gray-500 border border-dashed border-gray-200">
        <p className="font-medium text-textPrimary">Sélectionnez un shop</p>
        <p className="text-sm mt-1 max-w-md mx-auto">
          Choisissez le shop courant dans l&apos;en-tête pour afficher la file d&apos;attente de réception.
        </p>
      </div>
    );
  }

  if (dashError) {
    return (
      <div className="card bg-red-50 border border-red-200 text-red-700 p-4 flex justify-between items-center">
        <span>{dashError}</span>
        <button className="underline text-sm" onClick={() => void refreshNow()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <ToastComponent />

      <ConfirmModal
        isOpen={!!checkInConfirm}
        title="Enregistrer l'arrivée ?"
        message={
          checkInConfirm
            ? `Confirmer l'arrivée de ${checkInConfirm.clientFirstName} ${checkInConfirm.clientLastName} ? Le rendez-vous passera dans « Dans le shop ».`
            : ''
        }
        confirmLabel="Enregistrer"
        cancelLabel="Retour"
        confirmVariant="primary"
        isLoading={!!checkInConfirm && actionLoading === checkInConfirm.id}
        onClose={() => {
          if (checkInConfirm && actionLoading === checkInConfirm.id) return;
          setCheckInConfirm(null);
        }}
        onConfirm={() => checkInConfirm && void handleCheckIn(checkInConfirm.id)}
      />

      <ConfirmModal
        isOpen={!!undoCheckInConfirm}
        title="Annuler l'enregistrement ?"
        message={
          undoCheckInConfirm
            ? `${undoCheckInConfirm.clientFirstName} ${undoCheckInConfirm.clientLastName} ne sera plus indiqué comme présent. Le rendez-vous reviendra dans « À recevoir ».`
            : ''
        }
        confirmLabel="Annuler l'enregistrement"
        cancelLabel="Retour"
        confirmVariant="warning"
        isLoading={!!undoCheckInConfirm && actionLoading === undoCheckInConfirm.id}
        onClose={() => {
          if (undoCheckInConfirm && actionLoading === undoCheckInConfirm.id) return;
          setUndoCheckInConfirm(null);
        }}
        onConfirm={() => undoCheckInConfirm && void handleUndoCheckIn(undoCheckInConfirm.id)}
      />

      {/* Encaissement modal */}
      {checkoutTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          onClick={() => !checkoutSubmitting && setCheckoutTarget(null)}
          role="presentation"
        >
          <div
            className="card max-w-md w-full shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
          >
            <h3 id="checkout-modal-title" className="font-bold text-lg text-gray-900 mb-1">
              Encaissement — {checkoutTarget.clientFirstName} {checkoutTarget.clientLastName}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {checkoutTarget.serviceName}
              {checkoutTarget.assignedPractitionerFirstName && (
                <span className="block text-xs mt-1 text-gray-400">
                  Praticien : {checkoutTarget.assignedPractitionerFirstName}{' '}
                  {checkoutTarget.assignedPractitionerLastName ?? ''}
                </span>
              )}
            </p>

            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3 mb-4 text-xs">
              <p className="font-semibold text-violet-900 mb-2">Détail facturé (transmis par le praticien)</p>
              <CheckoutPricingBreakdown appointment={checkoutTarget} />
            </div>

            <div className="space-y-3">
              <div>
                <label htmlFor="checkout-promotion-value" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                  Promotion
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_9rem] gap-2">
                  <input
                    id="checkout-promotion-value"
                    type="text"
                    inputMode="decimal"
                    className="input-field w-full"
                    value={checkoutPromotionValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCheckoutPromotionValue(value);
                      setCheckoutAmount(getAmountAfterPromotion(
                        getCheckoutBaseAmount(checkoutTarget),
                        checkoutPromotionType,
                        value,
                      ));
                    }}
                    placeholder="Valeur de la promotion"
                    disabled={checkoutSubmitting}
                  />
                  <select
                    id="checkout-promotion-type"
                    aria-label="Type de promotion"
                    className="input-field w-full"
                    value={checkoutPromotionType}
                    onChange={(e) => {
                      const type = e.target.value as CheckoutPromotionType;
                      setCheckoutPromotionType(type);
                      setCheckoutAmount(getAmountAfterPromotion(
                        getCheckoutBaseAmount(checkoutTarget),
                        type,
                        checkoutPromotionValue,
                      ));
                    }}
                    disabled={checkoutSubmitting}
                  >
                    <option value="PERCENT">Pourcentage (%)</option>
                    <option value="AMOUNT">Montant (€)</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="checkout-amount" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                  Montant encaissé (€)
                </label>
                <input
                  id="checkout-amount"
                  type="text"
                  inputMode="decimal"
                  className="input-field w-full"
                  value={checkoutAmount}
                  onChange={(e) => setCheckoutAmount(e.target.value)}
                  placeholder="0"
                  disabled={checkoutSubmitting}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Calculé depuis le reste à encaisser, après promotion. Ajustez si besoin.
                </p>
              </div>
              <div>
                <label htmlFor="checkout-method" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                  Mode de paiement
                </label>
                <select
                  id="checkout-method"
                  className="input-field w-full"
                  value={checkoutMethod}
                  onChange={(e) => setCheckoutMethod(e.target.value as 'CASH' | 'CARD')}
                  disabled={checkoutSubmitting}
                >
                  <option value="CASH">Espèces</option>
                  <option value="CARD">Carte</option>
                </select>
              </div>
              <div>
                <label htmlFor="checkout-notes" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                  Note interne (optionnel)
                </label>
                <input
                  id="checkout-notes"
                  type="text"
                  className="input-field w-full"
                  value={checkoutNotes}
                  onChange={(e) => setCheckoutNotes(e.target.value)}
                  disabled={checkoutSubmitting}
                  placeholder="Ex. ticket n°…"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                type="button"
                className="btn-secondary flex-1 disabled:opacity-50"
                disabled={checkoutSubmitting}
                onClick={() => setCheckoutTarget(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-primary flex-1 disabled:opacity-50"
                disabled={checkoutSubmitting}
                onClick={() => void handleCompleteCheckout()}
              >
                {checkoutSubmitting ? 'Enregistrement…' : 'Valider le paiement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      {!embedded && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">{pageTitle}</h1>
            <p className="text-textSecondary text-sm mt-1 capitalize">
              {today}{currentShop?.name ? ` · ${currentShop.name}` : ''}
            hfghgf</p>
          </div>
          <RefreshWidget
            intervalSeconds={autoRefreshIntervalSeconds}
            refreshing={refreshing}
            lastRefreshAt={lastRefreshAt}
            onRefresh={() => void refreshNow()}
            disabled={shopLoading || !effectiveShopId}
          />
        </div>
      )}

      {/* Recherche + indicateurs */}
      {arrivalError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
          {arrivalError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.55fr)_minmax(360px,1fr)] gap-5 items-stretch">
        <ClientArrivalToolbar
          onError={setArrivalError}
          onClientAction={() => void loadDashboard(false)}
        />

        <div className="grid grid-cols-2 gap-3">
        <div className="card bg-surface-container-low text-center py-5">
          <p className="text-4xl font-bold text-primary-500">{String(stats.toReceive).padStart(2, '0')}</p>
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mt-1">À recevoir</p>
        </div>
        <div className="card bg-amber-50 text-center py-5">
          <p className="text-4xl font-bold text-amber-500">{String(stats.inShop).padStart(2, '0')}</p>
          <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mt-1">Dans le shop</p>
        </div>
        <div className="card bg-violet-50 text-center py-5 ring-1 ring-violet-200/80">
          <p className="text-4xl font-bold text-violet-600">{String(stats.awaitingCheckout).padStart(2, '0')}</p>
          <p className="text-xs text-violet-700 font-semibold uppercase tracking-wider mt-1">À encaisser</p>
        </div>
        <div className="card text-center py-5">
          <p className="text-4xl font-bold text-gray-700">{String(stats.totalToday).padStart(2, '0')}</p>
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mt-1">Total du jour</p>
          </div>
        </div>
      </div>

      {/* Section: awaiting checkout */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wider text-violet-800 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
          À encaisser (prestation terminée)
          <span className="ml-auto text-gray-500 font-normal normal-case tracking-normal text-xs">
            En attente du paiement en caisse
          </span>
        </h2>

        {awaitingCheckout.length === 0 ? (
          <div className="card text-center py-8 text-gray-400 border border-dashed border-gray-200">
            <p className="text-sm font-medium">Aucun client en attente de caisse</p>
            <p className="text-xs mt-1 max-w-md mx-auto">
              Lorsque le praticien valide la dernière étape du parcours piercing, le rendez-vous apparaît ici pour encaissement.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {awaitingCheckout.map((a) => (
              <div key={a.id} className="card flex flex-col gap-3 border-l-4 border-violet-500 bg-violet-50/30">
                <div className="flex gap-3 items-start">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-bold bg-violet-100 text-violet-800">
                    {initials(a.clientFirstName, a.clientLastName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-textPrimary truncate">
                      {a.clientFirstName} {a.clientLastName}
                    </p>
                    <p className="text-xs text-textSecondary truncate">{a.clientEmail}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <StatusBadge status={a.status} />
                  <SourceBadge source={a.bookingSource} />
                </div>
                <div className="bg-white/80 rounded-lg p-3 border border-violet-100">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">{a.serviceName}</p>
                  {a.assignedPractitionerFirstName && (
                    <p className="text-xs text-gray-500">
                      Praticien : {a.assignedPractitionerFirstName} {a.assignedPractitionerLastName}
                    </p>
                  )}
                  <CheckoutPricingBreakdown appointment={a} />
                  <button
                    type="button"
                    className="mt-2 text-xs font-medium text-violet-700 hover:text-violet-900 hover:underline"
                    onClick={() => handleViewAppointment(a.id)}
                  >
                    Voir rendez-vous
                  </button>
                </div>
                <button
                  type="button"
                  className="btn-primary w-full py-3 font-semibold"
                  onClick={() => openCheckoutModal(a)}
                >
                  Encaisser et clôturer
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Section: to receive */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
          À recevoir aujourd'hui
          <span className="ml-auto text-gray-400 font-normal normal-case tracking-normal">
            {toReceive.length} rendez-vous
          </span>
        </h2>

        {toReceive.length === 0 ? (
          <div className="card text-center py-10 text-gray-400">
            <svg className="w-10 h-10 mx-auto mb-2 opacity-25" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
            </svg>
            <p className="text-sm font-medium">Aucun rendez-vous en attente de réception</p>
            <p className="text-xs mt-1">Les clients confirmés apparaîtront ici.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {toReceive.map((a) => (
              <AppointmentCard
                key={a.id}
                appointment={a}
                onRequestCheckIn={setCheckInConfirm}
                onViewAppointment={(appointment) => handleViewAppointment(appointment.id)}
                loading={actionLoading === a.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* Section: in shop */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          Dans le shop
          <span className="ml-auto text-gray-400 font-normal normal-case tracking-normal">
            {inShop.length} client{inShop.length > 1 ? 's' : ''}
          </span>
        </h2>

        {inShop.length === 0 ? (
          <div className="card text-center py-10 text-gray-400">
            <svg className="w-10 h-10 mx-auto mb-2 opacity-25" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
            </svg>
            <p className="text-sm font-medium">Personne dans le shop pour l'instant</p>
            <p className="text-xs mt-1">Les clients enregistrés à l'accueil apparaîtront ici.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {inShop.map((a) => (
              <AppointmentCard
                key={a.id}
                appointment={a}
                onRequestCheckIn={setCheckInConfirm}
                onRequestUndoCheckIn={setUndoCheckInConfirm}
                onViewAppointment={(appointment) => handleViewAppointment(appointment.id)}
                loading={actionLoading === a.id}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}